/* Tile Touch — Room Visualiser.
   Phase 1: assisted perspective mapping (upload photo, drag 4 corners, tile with grout + blend).
   Phase 2: in-browser ML — auto floor/wall detection (SegFormer via Transformers.js), obstacle
   occlusion, auto-extracted seamless tile texture from existing product images (no re-shoot),
   and a relight+occlusion composite for the downloaded image. ML always degrades gracefully to
   the manual 4-corner mode; the core flow never depends on ML. */
(function () {
  'use strict';
  var root = document.querySelector('[data-visualiser]');
  if (!root) return;
  var theme = window.theme || {};
  var COLL = '/collections/all-tiles';
  var LAYER = 1000;

  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
  function money(c) { var v = (parseFloat(c) || 0) / 100; return (theme.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, v.toFixed(2)); }

  /* ---------------- perspective maths (Phase 1) ---------------- */
  function solve(A, b) {
    var n = b.length, i, j, k;
    for (i = 0; i < n; i++) {
      var mx = i; for (j = i + 1; j < n; j++) if (Math.abs(A[j][i]) > Math.abs(A[mx][i])) mx = j;
      var tA = A[i]; A[i] = A[mx]; A[mx] = tA; var tb = b[i]; b[i] = b[mx]; b[mx] = tb;
      for (j = i + 1; j < n; j++) { var f = A[j][i] / A[i][i]; for (k = i; k < n; k++) A[j][k] -= f * A[i][k]; b[j] -= f * b[i]; }
    }
    var x = new Array(n);
    for (i = n - 1; i >= 0; i--) { var s = b[i]; for (j = i + 1; j < n; j++) s -= A[i][j] * x[j]; x[i] = s / A[i][i]; }
    return x;
  }
  function matrix3dFor(src, dst) {
    var A = [], b = [];
    for (var i = 0; i < 4; i++) { var s = src[i], d = dst[i];
      A.push([s[0], s[1], 1, 0, 0, 0, -s[0] * d[0], -s[1] * d[0]]); b.push(d[0]);
      A.push([0, 0, 0, s[0], s[1], 1, -s[0] * d[1], -s[1] * d[1]]); b.push(d[1]); }
    var h = solve(A, b);
    return 'matrix3d(' + [h[0], h[3], 0, h[6], h[1], h[4], 0, h[7], 0, 0, 1, 0, h[2], h[5], 0, 1].join(',') + ')';
  }

  /* ---------------- Phase 2: tile texture (Tier 1, zero-dep seamless crop) ---------------- */
  function toCv(img, w, h) { var c = document.createElement('canvas'); c.width = w; c.height = h; c.getContext('2d').drawImage(img, 0, 0, w, h); return c; }
  function findUniformSquare(img) {
    var nw = img.naturalWidth || img.width, nh = img.naturalHeight || img.height;
    var scale = Math.min(1, 512 / Math.max(nw, nh)); var W = Math.round(nw * scale), H = Math.round(nh * scale);
    var data = toCv(img, W, H).getContext('2d').getImageData(0, 0, W, H).data;
    var s = new Float64Array((W + 1) * (H + 1)), s2 = new Float64Array((W + 1) * (H + 1));
    function id(x, y) { return y * (W + 1) + x; }
    for (var y = 0; y < H; y++) for (var x = 0; x < W; x++) { var p = (y * W + x) * 4;
      var L = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
      s[id(x + 1, y + 1)] = L + s[id(x, y + 1)] + s[id(x + 1, y)] - s[id(x, y)];
      s2[id(x + 1, y + 1)] = L * L + s2[id(x, y + 1)] + s2[id(x + 1, y)] - s2[id(x, y)]; }
    var win = Math.max(8, Math.round(Math.min(W, H) * 0.55)), bx = Math.round(W * 0.06), by = Math.round(H * 0.06), step = Math.max(2, Math.round(win / 12));
    var best = { score: Infinity, x: bx, y: by, size: win };
    for (var yy = by; yy + win <= H - by; yy += step) for (var xx = bx; xx + win <= W - bx; xx += step) {
      var a = id(xx, yy), B = id(xx + win, yy), C = id(xx, yy + win), D = id(xx + win, yy + win), area = win * win;
      var sum = s[D] - s[B] - s[C] + s[a], sum2 = s2[D] - s2[B] - s2[C] + s2[a];
      var variance = sum2 / area - Math.pow(sum / area, 2);
      var cx = (xx + win / 2) / W - 0.5, cy = (yy + win / 2) / H - 0.5;
      var score = variance * (1 + 0.6 * (cx * cx + cy * cy));
      if (score < best.score) best = { score: score, x: xx, y: yy, size: win };
    }
    return { x: Math.round(best.x / scale), y: Math.round(best.y / scale), size: Math.round(best.size / scale) };
  }
  function makeSeamless(src, blendPx) {
    var S = src.width, out = document.createElement('canvas'); out.width = out.height = S; var g = out.getContext('2d'); g.drawImage(src, 0, 0);
    function strip(dir) {
      var b = Math.min(blendPx, Math.floor(S / 3)); var st = document.createElement('canvas'), sg;
      if (dir === 'x') { st.width = b; st.height = S; } else { st.width = S; st.height = b; } sg = st.getContext('2d'); sg.save();
      if (dir === 'x') { sg.translate(b, 0); sg.scale(-1, 1); sg.drawImage(src, S - b, 0, b, S, 0, 0, b, S); }
      else { sg.translate(0, b); sg.scale(1, -1); sg.drawImage(src, 0, S - b, S, b, 0, 0, S, b); } sg.restore();
      var grad = dir === 'x' ? sg.createLinearGradient(0, 0, b, 0) : sg.createLinearGradient(0, 0, 0, b);
      grad.addColorStop(0, 'rgba(0,0,0,1)'); grad.addColorStop(1, 'rgba(0,0,0,0)');
      sg.globalCompositeOperation = 'destination-in'; sg.fillStyle = grad; sg.fillRect(0, 0, st.width, st.height); g.drawImage(st, 0, 0);
    }
    strip('x'); strip('y'); return out;
  }
  function tileTexture(img, size) {
    size = size || 240;
    try {
      var u = findUniformSquare(img);
      var cell = document.createElement('canvas'); cell.width = cell.height = size;
      cell.getContext('2d').drawImage(img, u.x, u.y, u.size, u.size, 0, 0, size, size);
      return makeSeamless(cell, Math.round(size / 9));
    } catch (e) { // CORS taint or tiny image -> plain fill
      var c = document.createElement('canvas'); c.width = c.height = size; c.getContext('2d').drawImage(img, 0, 0, size, size); return c;
    }
  }

  /* ---------------- Phase 2: ML segmentation (lazy, graceful) ---------------- */
  var SEG = { m: null, loading: null, failed: false };
  var TXCDN = 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@4.2.0';
  function getSegmenter(onProgress) {
    if (SEG.m) return Promise.resolve(SEG.m);
    if (SEG.failed) return Promise.resolve(null);
    if (SEG.loading) return SEG.loading;
    SEG.loading = (async function () {
      try {
        onProgress && onProgress('download', 0.02);
        var mod = await import(/* webpackIgnore: true */ TXCDN + '/+esm');
        var device = 'wasm';
        try { if (navigator.gpu && await navigator.gpu.requestAdapter()) device = 'webgpu'; } catch (e) {}
        var seg = await mod.pipeline('image-segmentation', 'Xenova/segformer-b0-finetuned-ade-512-512', {
          dtype: 'q8', device: device,
          progress_callback: function (p) { if (p && p.status === 'progress' && p.total) onProgress && onProgress('download', Math.min(0.92, p.loaded / p.total)); }
        });
        SEG.m = seg; onProgress && onProgress('ready', 1); return seg;
      } catch (err) { console.warn('[TileTouch] segmentation load failed -> assisted mode', err); SEG.failed = true; onProgress && onProgress('failed', 0); return null; }
      finally { SEG.loading = null; }
    })();
    return SEG.loading;
  }
  var FLOOR = ['floor', 'rug', 'earth', 'flooring'];
  var OBST = ['sofa', 'chair', 'table', 'armchair', 'coffee table', 'person', 'cabinet', 'bed', 'plant', 'pot', 'stool', 'bench', 'ottoman', 'sink', 'toilet', 'bathtub'];
  function maskCanvas(parts, W, H) {
    if (!parts.length) return null;
    var mW = parts[0].mask.width, mH = parts[0].mask.height, small = document.createElement('canvas'); small.width = mW; small.height = mH;
    var sctx = small.getContext('2d'), im = sctx.createImageData(mW, mH);
    for (var i = 0; i < mW * mH; i++) { var on = 0; for (var k = 0; k < parts.length; k++) if (parts[k].mask.data[i] > 127) { on = 1; break; } var j = i * 4; im.data[j] = im.data[j + 1] = im.data[j + 2] = 255; im.data[j + 3] = on ? 255 : 0; }
    sctx.putImageData(im, 0, 0);
    var out = document.createElement('canvas'); out.width = W; out.height = H; out.getContext('2d').drawImage(small, 0, 0, W, H); return out;
  }
  function defaultQuad(W, H) { return [{ x: W * 0.3, y: H * 0.45 }, { x: W * 0.7, y: H * 0.45 }, { x: W * 0.98, y: H * 0.98 }, { x: W * 0.02, y: H * 0.98 }]; }
  function estimateQuad(floorC, W, H) {
    var d = floorC.getContext('2d').getImageData(0, 0, W, H).data, rowMin = new Int32Array(H).fill(-1), rowMax = new Int32Array(H).fill(-1);
    var rows = 0, topRow = H, area = 0;
    for (var y = 0; y < H; y++) { var lo = -1, hi = -1, off = y * W * 4; for (var x = 0; x < W; x++) if (d[off + x * 4 + 3] > 8) { if (lo < 0) lo = x; hi = x; area++; } rowMin[y] = lo; rowMax[y] = hi; if (lo >= 0) { rows++; if (y < topRow) topRow = y; } }
    if (rows < H * 0.05) return { corners: defaultQuad(W, H), confidence: 0.15, isDefault: true };
    var band = Math.max(3, Math.round(H * 0.08)), nl = 0, nr = 0, n = 0;
    for (var y2 = H - 1; y2 >= 0 && n < band; y2--) if (rowMin[y2] >= 0) { nl += rowMin[y2]; nr += rowMax[y2]; n++; }
    var nearL = nl / n, nearR = nr / n, nearY = H - 1, fl = 0, fr = 0, m = 0;
    for (var y3 = topRow; y3 < H && m < Math.max(2, Math.round(H * 0.04)); y3++) if (rowMin[y3] >= 0) { fl += rowMin[y3]; fr += rowMax[y3]; m++; }
    var farL = fl / m, farR = fr / m, farY = topRow;
    var corners = [{ x: farL, y: farY }, { x: farR, y: farY }, { x: nearR, y: nearY }, { x: nearL, y: nearY }];
    var nearW = nearR - nearL, farW = farR - farL, converges = nearW > farW * 1.02, notDeg = nearW > W * 0.1 && farY < nearY - H * 0.1;
    var coverage = area / (rows * (nearW || 1)), sym = 1 - Math.min(1, Math.abs((W - nearR) - nearL) / (W * 0.5));
    var c = 0.35; if (converges) c += 0.25; if (notDeg) c += 0.2; if (coverage > 0.75) c += 0.1; c += 0.1 * sym; c = Math.max(0, Math.min(1, c));
    if (!notDeg) return { corners: defaultQuad(W, H), confidence: 0.15, isDefault: true };
    return { corners: corners, confidence: c, isDefault: false };
  }
  async function detectSurface(photoEl, onProgress) {
    var seg = await getSegmenter(onProgress); if (!seg) return null;
    var W = photoEl.naturalWidth || photoEl.width, H = photoEl.naturalHeight || photoEl.height, results;
    try { onProgress && onProgress('analyse', 0.97); results = await seg(photoEl.currentSrc || photoEl.src); } catch (e) { console.warn(e); return null; }
    var floorC = maskCanvas(results.filter(function (r) { return FLOOR.indexOf(r.label) >= 0; }), W, H);
    if (!floorC) return { floorMask: null, obstacleMask: null, corners: defaultQuad(W, H), confidence: 0.15, isDefault: true, natW: W, natH: H };
    var obstacleMask = maskCanvas(results.filter(function (r) { return OBST.indexOf(r.label) >= 0; }), W, H);
    var q = estimateQuad(floorC, W, H);
    return { floorMask: floorC, obstacleMask: obstacleMask, corners: q.corners, confidence: q.confidence, isDefault: !!q.isDefault, natW: W, natH: H };
  }

  /* ---------------- Phase 2: composite (relight + occlusion) for download ---------------- */
  function warpTri(ctx, img, s, d) {
    ctx.save(); ctx.beginPath(); ctx.moveTo(d[0][0], d[0][1]); ctx.lineTo(d[1][0], d[1][1]); ctx.lineTo(d[2][0], d[2][1]); ctx.closePath(); ctx.clip();
    var sx0 = s[0][0], sy0 = s[0][1], sx1 = s[1][0], sy1 = s[1][1], sx2 = s[2][0], sy2 = s[2][1];
    var dx0 = d[0][0], dy0 = d[0][1], dx1 = d[1][0], dy1 = d[1][1], dx2 = d[2][0], dy2 = d[2][1];
    var den = sx0 * (sy2 - sy1) - sx1 * sy2 + sx2 * sy1 + (sx1 - sx2) * sy0; if (den === 0) { ctx.restore(); return; }
    var m11 = -(sy0 * (dx2 - dx1) - sy1 * dx2 + sy2 * dx1 + (sy1 - sy2) * dx0) / den;
    var m12 = (sy1 * dy2 + sy0 * (dy1 - dy2) - sy2 * dy1 + (sy2 - sy1) * dy0) / den;
    var m21 = (sx0 * (dx2 - dx1) - sx1 * dx2 + sx2 * dx1 + (sx1 - sx2) * dx0) / den;
    var m22 = -(sx1 * dy2 + sx0 * (dy1 - dy2) - sx2 * dy1 + (sx2 - sx1) * dy0) / den;
    var dx = (sx0 * (sy2 * dx1 - sy1 * dx2) + sy0 * (sx1 * dx2 - sx2 * dx1) + (sx2 * sy1 - sx1 * sy2) * dx0) / den;
    var dy = (sx0 * (sy2 * dy1 - sy1 * dy2) + sy0 * (sx1 * dy2 - sx2 * dy1) + (sx2 * sy1 - sx1 * sy2) * dy0) / den;
    ctx.transform(m11, m12, m21, m22, dx, dy); ctx.drawImage(img, 0, 0); ctx.restore();
  }
  function drawWarped(ctx, img, c) {
    var q = c.map(function (p) { return [p.x, p.y]; });
    warpTri(ctx, img, [[0, 0], [img.width, 0], [0, img.height]], [q[0], q[1], q[3]]);
    warpTri(ctx, img, [[img.width, 0], [img.width, img.height], [0, img.height]], [q[1], q[2], q[3]]);
  }
  function composite(out, photoEl, tiled, corners, opts) {
    opts = opts || {}; var W = out.width, H = out.height, octx = out.getContext('2d');
    octx.clearRect(0, 0, W, H); octx.drawImage(photoEl, 0, 0, W, H);
    var mask = document.createElement('canvas'); mask.width = W; mask.height = H; var mctx = mask.getContext('2d');
    mctx.fillStyle = '#fff'; mctx.beginPath(); mctx.moveTo(corners[0].x, corners[0].y);
    for (var i = 1; i < corners.length; i++) mctx.lineTo(corners[i].x, corners[i].y); mctx.closePath(); mctx.fill();
    if (opts.floorMask) { mctx.globalCompositeOperation = 'destination-in'; mctx.drawImage(opts.floorMask, 0, 0, W, H); mctx.globalCompositeOperation = 'source-over'; }
    if (opts.obstacleMask) { mctx.globalCompositeOperation = 'destination-out'; mctx.drawImage(opts.obstacleMask, 0, 0, W, H); mctx.globalCompositeOperation = 'source-over'; }
    var relit = null;
    if (opts.relight !== false) {
      var wc = document.createElement('canvas'); wc.width = W; wc.height = H; var wctx = wc.getContext('2d'); wctx.drawImage(photoEl, 0, 0, W, H);
      var d = wctx.getImageData(0, 0, W, H).data, md = mask.getContext('2d').getImageData(0, 0, W, H).data, sum = 0, n = 0;
      for (var q = 0; q < d.length; q += 4) if (md[q + 3] > 8) { sum += 0.2126 * d[q] + 0.7152 * d[q + 1] + 0.0722 * d[q + 2]; n++; }
      var mean = n ? sum / n : 128, strength = opts.relightStrength == null ? 1 : opts.relightStrength;
      var ri = wctx.createImageData(W, H), rd = ri.data;
      for (var p = 0; p < d.length; p += 4) { var g = (0.2126 * d[p] + 0.7152 * d[p + 1] + 0.0722 * d[p + 2]) / mean * 128; g = 128 + (g - 128) * strength; g = Math.max(0, Math.min(255, g)); rd[p] = rd[p + 1] = rd[p + 2] = g; rd[p + 3] = 255; }
      relit = document.createElement('canvas'); relit.width = W; relit.height = H; relit.getContext('2d').putImageData(ri, 0, 0);
    }
    var comp = document.createElement('canvas'); comp.width = W; comp.height = H; var cctx = comp.getContext('2d');
    drawWarped(cctx, tiled, corners);
    if (relit) { cctx.globalCompositeOperation = 'multiply'; cctx.drawImage(relit, 0, 0); }
    cctx.globalCompositeOperation = 'destination-in'; cctx.drawImage(mask, 0, 0); cctx.globalCompositeOperation = 'source-over';
    octx.globalAlpha = opts.opacity == null ? 0.94 : opts.opacity; octx.drawImage(comp, 0, 0); octx.globalAlpha = 1;
    return out;
  }

  /* ---------------- state ---------------- */
  var state = { photo: null, surface: 'floor', corners: null, tile: null, grout: '#d9d2c6', groutW: 3, scale: 90, rotate: 0, blend: 'multiply', opacity: 0.92, masks: { floor: null, obstacle: null }, relight: true, autoConf: 0 };

  /* ---------------- DOM ---------------- */
  root.innerHTML =
    '<div class="viz">' +
      '<div class="viz-stage" data-stage>' +
        '<div class="viz-empty" data-empty><div class="viz-empty__inner"><div class="viz-empty__icon">📷</div>' +
          '<p class="viz-empty__t">Upload a photo of your room</p><p class="viz-empty__s">We\'ll find your floor and lay the tiles in — or just drag the four dots.</p>' +
          '<label class="btn btn--primary viz-upload">Upload a photo<input type="file" accept="image/*" hidden data-file></label></div></div>' +
        '<img class="viz-photo" data-photo alt="" hidden>' +
        '<div class="viz-tiles" data-tiles hidden></div>' +
        '<canvas class="viz-occ" data-occ hidden></canvas>' +
        '<svg class="viz-quad" data-quad hidden><polygon data-poly></polygon></svg>' +
        '<div class="viz-handles" data-handles hidden></div>' +
        '<div class="viz-loading" data-loading hidden><div class="quiz-spinner"></div><p data-loadtext>Loading smart detection…</p></div>' +
      '</div>' +
      '<div class="viz-panel" data-panel hidden>' +
        '<button type="button" class="btn btn--primary viz-auto" data-auto hidden>✨ Auto-detect my floor</button>' +
        '<p class="viz-autosub" data-autosub hidden>We\'ll find your floor and lay the tiles in for you.</p>' +
        '<div class="viz-toast" data-toast hidden></div>' +
        '<div class="viz-row"><span class="viz-label">Surface</span><div class="viz-seg" data-surface><button data-surf="floor" class="is-on">Floor</button><button data-surf="wall">Wall</button></div></div>' +
        '<div class="viz-row"><span class="viz-label">Choose a tile</span></div>' +
        '<div class="viz-tilepicker" data-tilepicker></div>' +
        '<div class="viz-row"><span class="viz-label">Grout</span><div class="viz-groutcols" data-grout>' +
          ['#efe9dd','#d9d2c6','#b7ab97','#8a8072','#3a3632'].map(function(c){return "<button data-g='"+c+"' style='background:"+c+"'"+(c==='#d9d2c6'?" class='is-on'":"")+"></button>";}).join('') + '</div></div>' +
        '<div class="viz-row"><span class="viz-label">Tile size</span><input type="range" min="40" max="180" value="90" data-scale></div>' +
        '<div class="viz-row"><span class="viz-label">Grout width</span><input type="range" min="0" max="10" value="3" data-groutw></div>' +
        '<div class="viz-row"><span class="viz-label">Layout</span><div class="viz-seg" data-rot><button data-r="0" class="is-on">Straight</button><button data-r="45">Diagonal</button></div></div>' +
        '<div class="viz-row"><span class="viz-label">Realistic shadows</span><label class="viz-switch"><input type="checkbox" data-relight checked><span></span></label></div>' +
        '<div class="viz-actions">' +
          '<label class="btn btn--ghost viz-rephoto">Change photo<input type="file" accept="image/*" hidden data-file></label>' +
          '<button class="btn btn--ghost" data-reset>Reset corners</button>' +
          '<button class="btn btn--primary" data-download>Download image</button>' +
        '</div>' +
        '<p class="viz-note">Auto-detect works best on clear, straight-on room photos. You can always fine-tune the dots.</p>' +
      '</div>' +
    '</div>';

  var stage = root.querySelector('[data-stage]'), photoEl = root.querySelector('[data-photo]'), tilesEl = root.querySelector('[data-tiles]');
  var occEl = root.querySelector('[data-occ]'), quadSvg = root.querySelector('[data-quad]'), polyEl = root.querySelector('[data-poly]');
  var handlesEl = root.querySelector('[data-handles]'), panel = root.querySelector('[data-panel]'), emptyEl = root.querySelector('[data-empty]');
  var autoBtn = root.querySelector('[data-auto]'), autoSub = root.querySelector('[data-autosub]'), toastEl = root.querySelector('[data-toast]');
  var loadingEl = root.querySelector('[data-loading]'), loadText = root.querySelector('[data-loadtext]');

  function px(n) { return n + 'px'; }
  function stageSize() { var r = stage.getBoundingClientRect(); return { w: r.width, h: r.height }; }
  function toast(msg, kind) { toastEl.textContent = msg; toastEl.className = 'viz-toast is-' + (kind || 'ok'); toastEl.hidden = false; clearTimeout(toast._t); toast._t = setTimeout(function () { toastEl.hidden = true; }, 5000); }

  function cellDataURL(cb) {
    if (!state.tile || !state.tile._img) { cb(null); return; }
    var S = 240, g = state.groutW * 2, face = tileTexture(state.tile._img, S);
    var c = document.createElement('canvas'); c.width = c.height = S; var ctx = c.getContext('2d');
    ctx.fillStyle = state.grout; ctx.fillRect(0, 0, S, S);
    try { ctx.drawImage(face, g, g, S - 2 * g, S - 2 * g); } catch (e) {}
    try { cb(c.toDataURL('image/jpeg', 0.88)); } catch (e) { cb(null); }
  }
  function defaultCorners() {
    var s = stageSize();
    if (state.surface === 'floor') return [{ x: s.w * 0.28, y: s.h * 0.52 }, { x: s.w * 0.72, y: s.h * 0.52 }, { x: s.w * 0.96, y: s.h * 0.94 }, { x: s.w * 0.04, y: s.h * 0.94 }];
    return [{ x: s.w * 0.08, y: s.h * 0.10 }, { x: s.w * 0.92, y: s.h * 0.10 }, { x: s.w * 0.92, y: s.h * 0.78 }, { x: s.w * 0.08, y: s.h * 0.78 }];
  }

  function renderTiles() {
    if (!state.corners || !state.tile) { tilesEl.hidden = true; return; }
    cellDataURL(function (url) {
      if (!url) { tilesEl.hidden = true; return; }
      var src = [[0, 0], [LAYER, 0], [LAYER, LAYER], [0, LAYER]], dst = state.corners.map(function (c) { return [c.x, c.y]; });
      tilesEl.hidden = false; tilesEl.style.width = px(LAYER); tilesEl.style.height = px(LAYER);
      tilesEl.style.backgroundImage = 'url(' + url + ')'; tilesEl.style.backgroundSize = state.scale + 'px ' + state.scale + 'px';
      tilesEl.style.transformOrigin = '0 0'; tilesEl.style.mixBlendMode = state.blend; tilesEl.style.opacity = state.opacity;
      tilesEl.classList.toggle('is-diagonal', state.rotate === 45);
      tilesEl.style.transform = matrix3dFor(src, dst);
      renderOcclusion();
    });
  }
  function renderOcclusion() {
    // draw obstacle pixels from the photo ON TOP of the tiles so furniture stays in front (live)
    if (!state.masks.obstacle || !state.photo) { occEl.hidden = true; return; }
    var s = stageSize(); occEl.width = s.w; occEl.height = s.h; occEl.style.width = px(s.w); occEl.style.height = px(s.h);
    var ctx = occEl.getContext('2d'); ctx.clearRect(0, 0, s.w, s.h);
    try { ctx.drawImage(photoEl, 0, 0, s.w, s.h); ctx.globalCompositeOperation = 'destination-in'; ctx.drawImage(state.masks.obstacle, 0, 0, s.w, s.h); ctx.globalCompositeOperation = 'source-over'; occEl.hidden = false; } catch (e) { occEl.hidden = true; }
  }
  function renderQuad() {
    if (!state.corners) { quadSvg.hidden = true; handlesEl.hidden = true; return; }
    var s = stageSize(); quadSvg.setAttribute('viewBox', '0 0 ' + s.w + ' ' + s.h); quadSvg.setAttribute('width', s.w); quadSvg.setAttribute('height', s.h); quadSvg.hidden = false;
    polyEl.setAttribute('points', state.corners.map(function (c) { return c.x + ',' + c.y; }).join(' '));
    handlesEl.hidden = false; handlesEl.innerHTML = '';
    state.corners.forEach(function (c, i) { var h = document.createElement('button'); h.className = 'viz-handle'; h.setAttribute('data-h', i); h.style.left = px(c.x); h.style.top = px(c.y); handlesEl.appendChild(h); });
  }
  function renderAll() { renderQuad(); renderTiles(); }

  /* drag */
  var drag = null;
  function pointerPos(e) { var r = stage.getBoundingClientRect(), t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; }
  handlesEl.addEventListener('pointerdown', function (e) { var h = e.target.closest('[data-h]'); if (!h) return; drag = +h.getAttribute('data-h'); h.setPointerCapture && h.setPointerCapture(e.pointerId); e.preventDefault(); });
  window.addEventListener('pointermove', function (e) { if (drag == null) return; var p = pointerPos(e), s = stageSize(); state.corners[drag] = { x: Math.max(0, Math.min(s.w, p.x)), y: Math.max(0, Math.min(s.h, p.y)) }; renderAll(); });
  window.addEventListener('pointerup', function () { drag = null; });

  /* photo upload — set stage aspect to photo so image px <-> stage px is a clean scale */
  function loadPhoto(file) {
    if (!file) return; var url = URL.createObjectURL(file);
    photoEl.onload = function () {
      stage.style.aspectRatio = (photoEl.naturalWidth || 4) + '/' + (photoEl.naturalHeight || 3);
      photoEl.hidden = false; emptyEl.hidden = true; panel.hidden = false; autoBtn.hidden = false; autoSub.hidden = false;
      state.masks = { floor: null, obstacle: null }; occEl.hidden = true;
      state.corners = defaultCorners(); renderAll();
    };
    photoEl.src = url; state.photo = url;
  }
  root.querySelectorAll('[data-file]').forEach(function (inp) { inp.addEventListener('change', function (e) { loadPhoto(e.target.files[0]); }); });

  /* tile picker */
  function setTile(t) { var img = new Image(); img.crossOrigin = 'anonymous'; img.onload = function () { t._img = img; state.tile = t; renderTiles(); }; img.onerror = function () { t._img = null; state.tile = t; renderTiles(); }; img.src = t.img; }
  function loadTilePicker() {
    var wrap = root.querySelector('[data-tilepicker]'), qp = new URLSearchParams(location.search);
    var initial = qp.get('tile') || root.getAttribute('data-initial-img'), initialTitle = qp.get('title') || 'This tile';
    fetch(COLL + '?view=quizdata').then(function (r) { return r.json(); }).then(function (d) {
      var items = (d.items || []).filter(function (x) { return x.img; }).slice(0, 24);
      if (initial) items.unshift({ img: initial, t: initialTitle });
      wrap.innerHTML = items.map(function (x, i) { return "<button class='viz-tilechip" + (i === 0 ? " is-on" : "") + "' data-i='" + i + "' title='" + esc(x.t || '') + "' style='background-image:url(" + x.img + ")'></button>"; }).join('');
      wrap.querySelectorAll('[data-i]').forEach(function (b) { b.addEventListener('click', function () { wrap.querySelectorAll('.viz-tilechip').forEach(function (x) { x.classList.remove('is-on'); }); b.classList.add('is-on'); var it = items[+b.getAttribute('data-i')]; setTile({ img: it.img, title: it.t }); }); });
      if (items[0]) setTile({ img: items[0].img, title: items[0].t });
    }).catch(function () { wrap.innerHTML = "<p class='viz-note'>Couldn't load tiles — try refreshing.</p>"; });
  }

  /* controls */
  root.querySelector('[data-surface]').addEventListener('click', function (e) { var b = e.target.closest('[data-surf]'); if (!b) return; this.querySelectorAll('button').forEach(function (x) { x.classList.remove('is-on'); }); b.classList.add('is-on'); state.surface = b.getAttribute('data-surf'); if (state.photo) { state.corners = defaultCorners(); renderAll(); } });
  root.querySelector('[data-grout]').addEventListener('click', function (e) { var b = e.target.closest('[data-g]'); if (!b) return; this.querySelectorAll('button').forEach(function (x) { x.classList.remove('is-on'); }); b.classList.add('is-on'); state.grout = b.getAttribute('data-g'); renderTiles(); });
  root.querySelector('[data-rot]').addEventListener('click', function (e) { var b = e.target.closest('[data-r]'); if (!b) return; this.querySelectorAll('button').forEach(function (x) { x.classList.remove('is-on'); }); b.classList.add('is-on'); state.rotate = +b.getAttribute('data-r'); renderTiles(); });
  root.querySelector('[data-scale]').addEventListener('input', function () { state.scale = +this.value; renderTiles(); });
  root.querySelector('[data-groutw]').addEventListener('input', function () { state.groutW = +this.value; renderTiles(); });
  root.querySelector('[data-relight]').addEventListener('change', function () { state.relight = this.checked; });
  root.querySelector('[data-reset]').addEventListener('click', function () { if (state.photo) { state.corners = defaultCorners(); renderAll(); } });

  /* auto-detect */
  autoBtn.addEventListener('click', async function () {
    autoBtn.disabled = true; loadingEl.hidden = false; loadText.textContent = 'Loading smart detection…';
    var res = await detectSurface(photoEl, function (phase, frac) {
      if (phase === 'download') loadText.textContent = 'Loading smart detection… ' + Math.round(frac * 100) + '%';
      else if (phase === 'analyse') loadText.textContent = 'Analysing your room…';
      else if (phase === 'failed') loadText.textContent = '';
    });
    loadingEl.hidden = true; autoBtn.disabled = false;
    if (!res) { toast('Auto-detect isn\'t available on this device — just drag the 4 dots.', 'warn'); autoBtn.hidden = true; autoSub.hidden = true; return; }
    var s = stageSize(), sx = s.w / res.natW, sy = s.h / res.natH;
    state.masks.floor = res.floorMask; state.masks.obstacle = res.obstacleMask; state.autoConf = res.confidence;
    state.corners = res.corners.map(function (c) { return { x: c.x * sx, y: c.y * sy }; });
    renderAll();
    if (res.confidence >= 0.6 && !res.isDefault) toast('Floor detected ✓ — drag any dot to fine-tune.', 'ok');
    else toast('Almost there — drag the 4 dots to your floor\'s corners.', 'warn');
    autoBtn.textContent = '✨ Re-run auto-detect';
  });

  /* download via composite (relight + occlusion) */
  root.querySelector('[data-download]').addEventListener('click', function () {
    if (!state.photo || !state.tile || !state.tile._img) return;
    var s = stageSize(), scaleUp = Math.min(2, (photoEl.naturalWidth || s.w) / s.w) || 1;
    var W = Math.round(s.w * scaleUp), H = Math.round(s.h * scaleUp);
    cellDataURL(function (url) {
      var pat = new Image(); pat.onload = function () {
        var pc = document.createElement('canvas'); pc.width = LAYER; pc.height = LAYER; var pctx = pc.getContext('2d');
        var p = pctx.createPattern(pat, 'repeat'); var sc = state.scale * scaleUp; pctx.imageSmoothingEnabled = true; pctx.scale(sc / 240, sc / 240); pctx.fillStyle = p; pctx.fillRect(0, 0, LAYER * 240 / sc, LAYER * 240 / sc);
        var cornersOut = state.corners.map(function (c) { return { x: c.x * scaleUp, y: c.y * scaleUp }; });
        var out = document.createElement('canvas'); out.width = W; out.height = H;
        composite(out, photoEl, pc, cornersOut, { relight: state.relight, floorMask: state.masks.floor, obstacleMask: state.masks.obstacle, opacity: state.opacity });
        var a = document.createElement('a'); a.download = 'tiletouch-my-room.jpg';
        try { a.href = out.toDataURL('image/jpeg', 0.92); a.click(); } catch (e) { toast('Could not export image on this device.', 'warn'); }
      }; pat.src = url;
    });
  });

  window.addEventListener('resize', function () { if (state.photo) renderAll(); });
  loadTilePicker();
})();
